import { Account, Holding } from '../../../../core'
import { ALLOCATION_KEYS, type AllocationKey } from '../../../../core/constants/Investments'
import { InvestmentAllocationCopy } from '../../../../core/types/localization/InvestmentsCopy'
import type { DonutData } from '../InvestmentsAllocationChart'
import { formatCurrency, formatPercentage } from '../../../../core/utils/NumberFormatting'
import { getHoldingGainLoss, getHoldingMarketValue } from '../../../../core/utils/InvestmentUtil'

export interface InvestmentsAllocationData {
  name: string
  displayName: string
  guid: string
  holdings: InvestmentAllocationTotals[]
  percentage: number
  value: number
  color: string
}
export interface InvestmentAllocationTotals {
  displayName: string
  name: string
  value: number
  percentage: number
}

const allocationColors: Record<AllocationKey, string> = {
  allocations_cash: '#AEDCCC',
  allocations_convertible: '#80CAD0',
  allocations_foreign_bond: '#51B9D4',
  allocations_foreign_stock: '#3DA0C9',
  allocations_other: '#3583B8',
  allocations_preferred: '#2D66A7',
  allocations_unknown: '#161D6A',
  allocations_us_bond: '#254995',
  allocations_us_stock: '#1E2D84',
}
const allocationsList = ALLOCATION_KEYS.map((name) => ({ name }))

// Synthetic bucket for the portion of holdings with no Morningstar asset-class
// data. The money IS invested (the slice expands into real holdings); we just
// can't break it into stock/bond/cash — hence "Unclassified", not "Unallocated".
const UNCLASSIFIED_KEY = 'allocations_unclassified'
const UNCLASSIFIED_COLOR = '#B7BFC9'

const inRange = (n: number, start: number, end?: number) => {
  const rangeStart = end === undefined ? 0 : start
  const rangeEnd = end === undefined ? start : end
  return n >= Math.min(rangeStart, rangeEnd) && n < Math.max(rangeStart, rangeEnd)
}

const createHoldingsData = (
  holdings: Holding[],
  allocationName: string,
  allocationValue: number,
) => {
  const uniqueHoldings = [
    ...new Set(holdings.map((holding: Holding) => holding.name.toLowerCase())),
  ]
  let displayName: string
  const investmentAllocationTotals = uniqueHoldings.map((holdingName) => {
    // sum all the holdings with the same "name"
    const holdingsTotalValue = holdings.reduce((acc, hold: Holding) => {
      const name = hold.name

      if (name.toLowerCase() === holdingName) {
        // Since we lose the capitalization with the unique names, we take it from the last match.
        displayName = name
        return (
          (getHoldingMarketValue(hold) * hold[allocationName as keyof typeof allocationColors]) /
            100 +
          acc
        )
      }

      return acc
    }, 0)

    return {
      displayName,
      name: displayName,
      value: holdingsTotalValue,
      percentage: holdingsTotalValue / allocationValue,
    }
  })
  return investmentAllocationTotals
}

export const createInvestmentsAllocationData = (
  accounts: Account[],
  copy: InvestmentAllocationCopy,
  holdings: Holding[],
) => {
  const allocationListDisplayNames = Object.values(copy.allocations_list)
  const investementAllocationData: InvestmentsAllocationData[] = []

  let totalAmount = 0

  const accountGuids = new Set(accounts.map(({ guid }) => guid))
  const updatedHoldings: Holding[] = holdings
    .filter(({ account_guid }) => accountGuids.has(account_guid))
    .map((eachHolding) => {
      return {
        ...eachHolding,
        name: eachHolding.symbol || eachHolding.description,
        // Falls back to purchase_price when cost_basis is unset, and contributes
        // 0 (not a fabricated gain) only when no basis is known at all.
        gainLoss: getHoldingGainLoss(eachHolding).gainLoss,
      }
    })

  const { cashBalance, totalAccountBalance } = accounts.reduce(
    (sum, account) => {
      return {
        cashBalance: sum.cashBalance + (account.cash_balance || 0),
        totalAccountBalance: sum.totalAccountBalance + (account.balance || 0),
      }
    },
    { cashBalance: 0, totalAccountBalance: 0 },
  )
  const { marketValue, totalGainLoss } = updatedHoldings.reduce(
    (acc, holding) => {
      return {
        marketValue: acc.marketValue + getHoldingMarketValue(holding),
        totalGainLoss: acc.totalGainLoss + holding.gainLoss,
      }
    },
    { marketValue: 0, totalGainLoss: 0 },
  )
  const totalMarketValue = marketValue + cashBalance

  // The unclassified portion of each holding (allocation weights sum to < 100%),
  // grouped by name so the slice can expand in the legend like a real bucket.
  const createUnclassifiedHoldingsData = (holdings: Holding[], unclassifiedTotal: number) => {
    const uniqueHoldings = [...new Set(holdings.map((holding) => holding.name.toLowerCase()))]
    const totals: InvestmentAllocationTotals[] = []

    uniqueHoldings.forEach((holdingName) => {
      let displayName = ''
      const unclassifiedValue = holdings.reduce((acc, hold) => {
        if (hold.name.toLowerCase() !== holdingName) return acc
        displayName = hold.name
        const allocationSum = ALLOCATION_KEYS.reduce(
          (sum, key) => sum + (Number(hold[key]) || 0),
          0,
        )
        const unclassifiedPercentage = Math.max(0, 100 - allocationSum) / 100
        return acc + getHoldingMarketValue(hold) * unclassifiedPercentage
      }, 0)

      // Guard with the same sub-cent epsilon as the outer slice so a
      // fully-classified holding whose weights sum to ~100 (float error) doesn't
      // produce a phantom "< $0.01" row in the breakdown.
      if (unclassifiedValue > 0.01) {
        totals.push({
          displayName,
          name: displayName,
          value: unclassifiedValue,
          percentage: unclassifiedTotal > 0 ? unclassifiedValue / unclassifiedTotal : 0,
        })
      }
    })

    return totals
  }

  allocationsList.forEach((allocation, index) => {
    const filteredHoldings = updatedHoldings.filter(
      (eachHolding) => !!eachHolding[allocation.name as keyof typeof allocationColors],
    )
    let holdingsTotal = filteredHoldings.reduce(
      (sum, holding) =>
        sum +
        (getHoldingMarketValue(holding) *
          holding[allocation.name as keyof typeof allocationColors]) /
          100,
      0,
    )
    const investmentAllocationTotals = createHoldingsData(
      filteredHoldings,
      allocation.name,
      holdingsTotal,
    )
    if (allocation.name === 'allocations_cash' && cashBalance > 0) {
      // Updating holdingsTotal to include cash balance
      // This affects the holdingsTotal and percentage for this allocation
      holdingsTotal += cashBalance

      // Creating a holding item for cash balance
      const cashBalanceHolding = {
        displayName: copy.cash_balance_display_name,
        name: 'allocations_name',
        value: cashBalance,
        percentage: cashBalance / totalMarketValue,
      }

      investmentAllocationTotals.push(cashBalanceHolding)
    }

    // Shows allocation if holdingsTotal is greater than zero
    if (holdingsTotal > 0) {
      investementAllocationData.push({
        name: allocation.name,
        displayName: allocationListDisplayNames[index],
        guid: `${allocation.name}-${index}`,
        holdings: investmentAllocationTotals,
        percentage: holdingsTotal / totalMarketValue,
        value: holdingsTotal,
        color: allocationColors[allocation.name as keyof typeof allocationColors],
      })
      totalAmount += holdingsTotal
    }
  })
  // Without this, holdings lacking asset-class data are dropped from the ring,
  // scaling the buckets that DO have data up to a misleading 100%. Surface the
  // gap as an explicit slice so the ring matches the center total and legend.
  const unclassifiedAmount = totalMarketValue - totalAmount
  if (unclassifiedAmount > 0.01) {
    investementAllocationData.push({
      name: UNCLASSIFIED_KEY,
      displayName: copy.unclassified_display_name,
      guid: UNCLASSIFIED_KEY,
      holdings: createUnclassifiedHoldingsData(updatedHoldings, unclassifiedAmount),
      percentage: unclassifiedAmount / totalMarketValue,
      value: unclassifiedAmount,
      color: UNCLASSIFIED_COLOR,
    })
    totalAmount += unclassifiedAmount
  }

  const donutData: DonutData[] = investementAllocationData.map(
    ({ displayName, guid, color, value }) => ({
      id: guid,
      color: color,
      label: displayName,
      value: totalMarketValue > 0 ? (value / totalMarketValue) * 100 : 0,
    }),
  )

  // When no holding in the filtered set has a known basis, the donut center
  // renders "—" rather than a fabricated $0.00 / 0% gain.
  const totalGainLossAvailable = updatedHoldings.some(
    (holding) => getHoldingGainLoss(holding).available,
  )

  return {
    investmentsData: investementAllocationData,
    donutData,
    totalAmount,
    totalGainLoss,
    totalGainLossAvailable,
    totalMarketValue,
    totalGainLossPercentage: totalGainLoss / totalAccountBalance,
  }
}

export const percentageDisplay = (value: number) =>
  value < 0.001 ? '< 0.1%' : formatPercentage(value)

export const currencyDisplay = (value: number) => {
  if (value === 0) return formatCurrency(value, '0,0.00')
  return inRange(value, -0.01, 0.01) ? '< $0.01' : formatCurrency(value, '0,0.00')
}
